import { installChatWindows } from './chat-window.js'
import App from '../Components/ChatBotUI/App.js?v=45.0.14'
import AudioCallHandler from '../Components/ChatBotUI/AudioCallHandler.js'
import { installSummarySourceReaderBridge } from '../Components/ChatBotUI/SummarySourceReadUI.js?v=1.0.1'

function ensureAiFallbackTestHarness() {
    if (typeof window === 'undefined') {
        return null
    }

    if (window.__lexiusAiFallbackTest && typeof window.logAiFallbackTestEvent === 'function') {
        if (typeof window.test_ai_fallback === 'undefined') {
            window.test_ai_fallback = false
        }
        return window.__lexiusAiFallbackTest
    }

    const STORAGE_KEY = 'lexius_ai_fallback_test_log_v1'
    const MAX_EVENTS = 500

    const safeClone = (value, depth = 0) => {
        if (depth > 4) return '[MaxDepth]'
        if (value === null || value === undefined) return value
        if (typeof value === 'string') {
            return value.length > 500 ? `${value.slice(0, 500)}...` : value
        }
        if (typeof value === 'number' || typeof value === 'boolean') return value
        if (value instanceof Error) {
            return {
                name: value.name,
                message: value.message,
                stack: value.stack ? value.stack.split('\n').slice(0, 5).join('\n') : null
            }
        }
        if (Array.isArray(value)) {
            return value.slice(0, 50).map(item => safeClone(item, depth + 1))
        }
        if (typeof value === 'object') {
            const clone = {}
            Object.keys(value).slice(0, 50).forEach(key => {
                clone[key] = safeClone(value[key], depth + 1)
            })
            return clone
        }
        return String(value)
    }

    const readStoredEvents = () => {
        try {
            const raw = window.localStorage ? window.localStorage.getItem(STORAGE_KEY) : null
            if (!raw) return []
            const parsed = JSON.parse(raw)
            return Array.isArray(parsed) ? parsed : []
        } catch (error) {
            console.warn('AI fallback test: failed to read stored log', error)
            return []
        }
    }

    const state = window.__lexiusAiFallbackTest || {
        sessionId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        events: readStoredEvents()
    }

    const persist = () => {
        try {
            if (window.localStorage) {
                window.localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(state.events.slice(-MAX_EVENTS))
                )
            }
        } catch (error) {
            console.warn('AI fallback test: failed to persist log', error)
        }
    }

    state.pushEvent = (scope, event, details = {}) => {
        const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            timestamp: new Date().toISOString(),
            sessionId: state.sessionId,
            scope,
            event,
            href: window.location.href,
            fallbackEnabled: window.test_ai_fallback === true,
            details: safeClone(details)
        }
        state.events.push(entry)
        if (state.events.length > MAX_EVENTS) {
            state.events = state.events.slice(-MAX_EVENTS)
        }
        persist()
        console.log(`[AI Fallback Test][${scope}] ${event}`, entry)
        return entry
    }

    window.__lexiusAiFallbackTest = state
    if (typeof window.test_ai_fallback === 'undefined') {
        window.test_ai_fallback = false
    }
    window.ensureAiFallbackTestHarness = () => state
    window.logAiFallbackTestEvent = (scope, event, details = {}) => state.pushEvent(scope, event, details)
    window.getAiFallbackTestLog = () => state.events.slice()
    window.dumpAiFallbackTestLog = () => JSON.stringify(state.events, null, 2)
    window.clearAiFallbackTestLog = () => {
        state.events = []
        persist()
        console.log('[AI Fallback Test] cleared log')
    }

    return state
}

ensureAiFallbackTestHarness()
installChatWindows()

//init vue
const { createApp } = Vue

installSummarySourceReaderBridge(createApp)

const vueApp = createApp(App)
    .mount('#chatBotUIApp')

try {
    const params = new URLSearchParams(window.location.search)
    const voiceDemo = params.get('demo_recording') === '1'
        && params.get('demo_feature') === 'voice-call'
    if (window.LEXIUS_E2E_TESTING === true) {
        window.lexiusChatApp = vueApp
    } else if (voiceDemo) {
        // Authentication can finish after Vue mounts. Check the current identity
        // when the isolated recorder accesses the usual transcript export action.
        Object.defineProperty(window, 'lexiusChatApp', {
            configurable: true,
            get: () => vueApp.currentUserIsRecordingAdmin() ? vueApp : undefined,
        })
    }
} catch (e) { }

    // Initialize Ably Realtime using token-based auth for all modules that rely on Ably
    ; (async () => {
        try {
            if (window.test_ai_fallback === true) {
                window.logAiFallbackTestEvent && window.logAiFallbackTestEvent('chat', 'ably-init-skipped', {
                    reason: 'test-ai-fallback'
                })
                return
            }
            if (typeof Ably !== 'undefined' && !window.ably_realtime_client) {
                const options = typeof window.getLexiusAblyClientOptions === 'function'
                    ? window.getLexiusAblyClientOptions('/ably/token')
                    : { authUrl: '/ably/token', recover: true }
                window.ably_realtime_client = new Ably.Realtime(options)
                await window.ably_realtime_client.connection.once('connected')
            }
        } catch (e) {
            console.error('Failed to initialize Ably Realtime', e)
        }
    })()



//tailwind custom configuration
tailwind.config = {
    future: {
        hoverOnlyWhenSupported: true
    },
    theme: {
        backgroundColor: {
            "theme-color": "var(--theme-color) !important",
            primary: "var(--primary) !important",
            fourth: "var(--fourth)",
            "body-bg": "var(--body-bg)",
            third: "var(--third)"
        },
        textColor: {
            secondary: "var(--text-secondary) !important",
            primary: "var(--text-primary) !important",
            "theme-color": "var(--text-theme-color) !important",
            fourth: "var(--fourth)",
            third: "var(--third)"
        },
        fill: {
            secondary: "var(--text-secondary) !important",
            primary: "var(--text-primary) !important",
            "theme-color": "var(--text-theme-color) !important"
        },
        backgroundImage: {
            "theme-color": "var(--theme-color) !important"
        },
        borderColor: {
            "theme-color": "var(--border-theme-color) !important",
            primary: "var(--border-primary)",
            fourth: "var(--border-fourth)"
        },
        fontFamily: {
            primary: "'Poppins', Sans-serif"
        }
    }
}
